import React, { useCallback, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { SOFTWARE_ROLES, getCurrentAppUser, normalizeSoftwareRole } from '../utils/accessControl';
import './Expenses.css';

const ALL_EXPENSE_STATUSES = ['Pending', 'Approved', 'Paid', 'Rejected'];
const ACCOUNT_EXPENSE_STATUSES = ['Approved', 'Paid', 'Rejected'];

const isAccountSoftwareRole = () => (
  normalizeSoftwareRole(getCurrentAppUser()?.softwareRole) === SOFTWARE_ROLES.ACCOUNT
);

function Expenses({ onLogout }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState(() => (isAccountSoftwareRole() ? 'Approved' : 'Pending'));
  const [paymentAccountCode, setPaymentAccountCode] = useState('11001');
  const accountRole = isAccountSoftwareRole();
  const availableStatuses = useMemo(
    () => (accountRole ? ACCOUNT_EXPENSE_STATUSES : ALL_EXPENSE_STATUSES),
    [accountRole]
  );

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/expenses', {
        params: { status: filterStatus },
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data.expenses || []);
    } catch (err) {
      setError('Failed to fetch expenses.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    if (!availableStatuses.includes(filterStatus)) {
      setFilterStatus(availableStatuses[0]);
      return;
    }

    fetchExpenses();
  }, [filterStatus, availableStatuses, fetchExpenses]);

  const handleAction = async (id, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this expense?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let url = `/api/expenses/${id}/${action}`;
      let data = {};
      if (action === 'pay') {
        data = { paymentAccountCode };
      }
      
      await axios.post(url, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh list
      fetchExpenses();
    } catch (err) {
      alert(`Failed to ${action} expense: ` + (err.response?.data?.message || err.message));
    }
  };

  const formatMoney = (amount) => `Tk ${Number(amount || 0).toFixed(2)}`;
  
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    return new Date(dateValue).toLocaleDateString('en-GB');
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <div className="main-section expenses-page">
        <header className="expenses-header">
          <h1>Expense Management</h1>
          <div className="expenses-filters">
            {availableStatuses.map(status => (
              <button 
                key={status} 
                className={`filter-btn ${filterStatus === status ? 'active' : ''}`}
                onClick={() => setFilterStatus(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </header>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="expenses-content">
          {loading ? (
            <p>Loading expenses...</p>
          ) : expenses.length === 0 ? (
            <p>No {filterStatus.toLowerCase()} expenses found.</p>
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Expense Head</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Photo</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(expense => (
                  <tr key={expense.ExpenseId}>
                    <td>{formatDate(expense.ExpenseDate)}</td>
                    <td>{expense.EmployeeName}</td>
                    <td>{expense.ExpenseHead}</td>
                    <td>{expense.Description || '-'}</td>
                    <td className="amount-col">{formatMoney(expense.Amount)}</td>
                    <td>
                      {expense.PhotoUrl ? (
                        <a href={expense.PhotoUrl} target="_blank" rel="noopener noreferrer" className="view-photo-link">
                          View
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="actions-col">
                      {filterStatus === 'Pending' && (
                        <>
                          <button className="btn-approve" onClick={() => handleAction(expense.ExpenseId, 'approve')}>Approve</button>
                          <button className="btn-reject" onClick={() => handleAction(expense.ExpenseId, 'reject')}>Reject</button>
                        </>
                      )}
                      {filterStatus === 'Approved' && (
                        <div className="pay-action">
                          <select 
                            value={paymentAccountCode} 
                            onChange={(e) => setPaymentAccountCode(e.target.value)}
                            title="Payment Account"
                          >
                            <option value="11001">Cash Account (11001)</option>
                            <option value="11002">Bank Account (11002)</option>
                          </select>
                          <button className="btn-pay" onClick={() => handleAction(expense.ExpenseId, 'pay')}>Pay</button>
                        </div>
                      )}
                      {['Paid', 'Rejected'].includes(filterStatus) && (
                        <span>{expense.Status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Expenses;
